import { React, useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Container,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  CssBaseline
} from "@mui/material";
import { CalendarMonth, Menu as MenuIcon } from "@mui/icons-material";
import { Paper } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DDay from "./components/calculators/DDay";
import FromTo from "./components/calculators/FromTo";
import DayCalculator from "./components/calculators/DayCalculator";
import BusinessDayCalculator from "./components/calculators/BusinessDayCalculator";
import RecurringDateCalculator from "./components/calculators/RecurringDateCalculator";
import WeekCalculator from "./components/calculators/WeekCalculator";
import { initGA, trackPageView } from './utils/analytics';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-KEB622E0G5'; // Replace with your actual Measurement ID

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
  boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 'calc(100vh - 64px)', // Account for average AppBar height
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '900px', // Prevent it from getting absurdly huge on ultrawide monitors
  [theme.breakpoints.up('xs')]: {
    width: '95%',
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('sm')]: {
    width: '85%',
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    width: '75%',
    padding: theme.spacing(5),
  },
  [theme.breakpoints.up('lg')]: {
    width: '65%',
    padding: theme.spacing(6),
  },
  background: 'linear-gradient(45deg, #FFFFFF 30%, #F5F5F5 90%)',
  boxShadow: '0 4px 10px 4px rgba(0, 0, 0, .1)', // Slightly larger shadow for bigger card
  borderRadius: 16, // More rounded corners for a premium feel
}));

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Initialize Google Analytics
    initGA(GA_MEASUREMENT_ID);
    // Track initial page view
    trackPageView(window.location.pathname);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileTabSelect = (index) => {
    setActiveTab(index);
    handleMobileMenuClose();
  };

  const calculators = [
    { label: t('nav.dDay'), component: <DDay /> },
    { label: t('nav.dateDiff'), component: <FromTo /> },
    { label: t('nav.dayCalc'), component: <DayCalculator /> },
    { label: t('nav.businessDays'), component: <BusinessDayCalculator /> },
    { label: t('nav.recurringDates'), component: <RecurringDateCalculator /> },
    { label: t('nav.weekCalc'), component: <WeekCalculator /> }
  ];

  const renderCalculator = () => {
    return calculators[activeTab]?.component || <DDay />;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <CssBaseline />
      <StyledAppBar position="static">
        <Toolbar disableGutters sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
          >
            <CalendarMonth sx={{ fontSize: 32 }} />
          </IconButton>
          <Typography
            variant={isMobile ? "h6" : "h4"}
            component="div"
            sx={{
              fontWeight: 'bold',
              letterSpacing: '1px',
              mr: 4, // Add right margin to separate from tabs
              flexGrow: isMobile ? 1 : 0, // Allow title to push hamburger menu to the right on mobile
            }}
          >
            DateCal
          </Typography>

          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="calculator menu"
                onClick={handleMobileMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  style: {
                    width: '250px',
                  },
                }}
              >
                {calculators.map((calc, index) => (
                  <MenuItem
                    key={index}
                    selected={activeTab === index}
                    onClick={() => handleMobileTabSelect(index)}
                    sx={{ py: 1.5, fontWeight: activeTab === index ? 'bold' : 'normal' }}
                  >
                    {calc.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                flexGrow: 1, // Allow tabs to take remaining space
                '& .MuiTab-root': {
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1.1rem' }, // scale tab fonts slightly
                  minHeight: '64px', // Match standard toolbar height
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#fff',
                  height: 3,
                }
              }}
            >
              {calculators.map((calc, index) => (
                <Tab key={index} label={calc.label} />
              ))}
            </Tabs>
          )}
        </Toolbar>
      </StyledAppBar>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ContentContainer maxWidth="lg">
          <StyledPaper>
            {renderCalculator()}
          </StyledPaper>
        </ContentContainer>
      </LocalizationProvider>
    </Box>
  );
}

export default App;
